import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import apiService, { Product } from '../../services/api';
import analytics from '../../services/analytics';

const SEOPageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const SEOHeader = styled.div`
  background: linear-gradient(135deg, var(--color-secondary) 0%, #1f1e3a 100%);
  color: white;
  padding: var(--spacing-12) 0;
  text-align: center;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h1 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: var(--spacing-4);
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  }

  .subtitle {
    font-size: 1.2rem;
    opacity: 0.9;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ContentSection = styled.section`
  padding: var(--spacing-12) 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    font-size: 2.5rem;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-6);
    text-align: center;
  }

  .intro-text {
    font-size: 1.1rem;
    line-height: 1.8;
    color: var(--color-text);
    max-width: 800px;
    margin: 0 auto var(--spacing-8) auto;
    text-align: center;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-6);
  margin-top: var(--spacing-8);
`;

const ProductCard = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
  }

  .product-image {
    width: 100%;
    height: 250px;
    background: linear-gradient(135deg, #f1f3f4 0%, #e8eaed 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 4rem;
    position: relative;
    overflow: hidden;
    padding: 16px;
    box-sizing: border-box;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      transition: transform 0.3s ease;
    }

    &:hover img {
      transform: scale(1.05);
    }
  }

  .product-content {
    padding: var(--spacing-6);

    .product-name {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--color-secondary);
      margin-bottom: var(--spacing-2);
      line-height: 1.3;
    }

    .product-description {
      color: var(--color-text-light);
      line-height: 1.6;
      margin-bottom: var(--spacing-4);
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .product-price {
      display: flex;
      align-items: center;
      gap: var(--spacing-2);
      margin-bottom: var(--spacing-4);

      .current-price {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--color-secondary);
      }

      .original-price {
        font-size: 1rem;
        color: var(--color-text-light);
        text-decoration: line-through;
      }

      .discount-badge {
        background: #dc3545;
        color: white;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 600;
      }
    }

    .view-product-btn {
      background: var(--color-secondary);
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: var(--border-radius);
      font-weight: 500;
      width: 100%;
      cursor: pointer;
      transition: background-color 0.3s ease;

      &:hover {
        background: #1f1e3a;
      }
    }
  }
`;

const BenefitsSection = styled.section`
  background: white;
  padding: var(--spacing-12) 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    text-align: center;
    margin-bottom: var(--spacing-8);
    color: var(--color-secondary);
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-6);
`;

const BenefitCard = styled.div`
  text-align: center;
  padding: var(--spacing-6);
  border-radius: var(--border-radius);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-3px);
  }

  .benefit-icon {
    font-size: 3rem;
    margin-bottom: var(--spacing-4);
  }

  h3 {
    font-size: 1.25rem;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-3);
  }

  p {
    color: var(--color-text-light);
    line-height: 1.6;
  }
`;

const CallToAction = styled.section`
  background: linear-gradient(135deg, var(--color-secondary) 0%, #1f1e3a 100%);
  color: white;
  padding: var(--spacing-12) 0;
  text-align: center;

  .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    font-size: 2.5rem;
    margin-bottom: var(--spacing-4);
  }

  p {
    font-size: 1.1rem;
    opacity: 0.9;
    margin-bottom: var(--spacing-6);
    line-height: 1.6;
  }

  .cta-button {
    background: white;
    color: var(--color-secondary);
    border: none;
    padding: var(--spacing-4) var(--spacing-8);
    border-radius: var(--border-radius-lg);
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(0,0,0,0.1);
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  font-size: 1.2rem;
  color: var(--color-text-light);
`;

interface SEOCategoryPageProps {}

const SEOCategoryPage: React.FC<SEOCategoryPageProps> = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<any>(null);
  type CategoryProduct = Product & { id: number };
  const [products, setProducts] = useState<CategoryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCategoryData();

    // Track page view for SEO analytics
    if (category) {
      analytics.trackPageView(`${category.name} | Premium Kvalitet | Lillaflora`);
    }
  }, [categorySlug, category]);

  const loadCategoryData = async () => {
    if (!categorySlug) return;

    setLoading(true);
    setError('');

    try {
      // Load category info
      const categoryData = await apiService.getCategoryBySlug(categorySlug);
      setCategory(categoryData);

      // Load products in this category
      const productsData = await apiService.getProducts({ category: categorySlug });
      const availableProducts = (productsData || []).filter(
        (product): product is CategoryProduct =>
          typeof product.id === 'number' && (product.inventory_quantity ?? 0) > 0
      );
      setProducts(availableProducts.slice(0, 12)); // Show max 12 in-stock products

    } catch (err: any) {
      console.error('Error loading category data:', err);
      setError('Kunde inte ladda kategoridata');
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product: CategoryProduct) => {
    navigate(`/produkter/${product.slug}`);
  };

  const handleViewAllProducts = () => {
    navigate(`/produkter?kategori=${categorySlug}`);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(price);
  };

  const getCategoryIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('ljus') || name.includes('doft')) return '🕯️';
    if (name.includes('dukning') || name.includes('servering')) return '🍽️';
    if (name.includes('växter') || name.includes('konstväxter')) return '🌿';
    if (name.includes('textiler')) return '🧵';
    if (name.includes('muggar') || name.includes('koppar')) return '☕';
    if (name.includes('godis')) return '🍬';
    if (name.includes('lyktor') || name.includes('dekoration')) return '🏮';
    return '✨';
  };

  const getCategoryDescription = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('doftljus') || name.includes('doftpinnar')) {
      return "Upptäck vårt exklusiva sortiment av doftljus och doftpinnar som fyller ditt hem med underbar doft och mysig stämning. Våra produkter är noggrant utvalda för att ge dig den bästa kvaliteten och längsta brinntiden.";
    }
    if (name.includes('dukning') || name.includes('servering')) {
      return "Skapa minnesvärda måltider med våra eleganta dukning- och serveringsprodukter. Från vackra tallrikar till stilrena bestick - allt för att göra dina middagar extra speciella.";
    }
    if (name.includes('konstväxter')) {
      return "Gör ditt hem grönt året runt med våra naturtrogna konstväxter. Perfekt för dig som vill ha grönska utan att behöva vattna eller sköta om riktiga växter.";
    }
    if (name.includes('ljus')) {
      return "Skapa den perfekta stämningen med våra vackra ljus. Från klassiska blockljus till moderna LED-ljus - vi har allt för att lysa upp ditt hem.";
    }
    if (name.includes('textiler')) {
      return "Förnya ditt hem med våra mjuka och stilrena textiler. Kuddar, plädar och överkast som ger värme och komfort till alla rum i hemmet.";
    }
    if (name.includes('muggar') || name.includes('koppar')) {
      return "Njut av dina favoritdrycker i våra vackra muggar och koppar. Perfekt för morgonkaffet eller en mysig kopp te på kvällen.";
    }
    if (name.includes('godis')) {
      return "Unna dig själv eller någon du tycker om med vårt lyxiga godis och andra söta överraskningar. Perfekt för presenter eller bara för att skämma bort dig själv.";
    }
    if (name.includes('lyktor') || name.includes('dekoration')) {
      return "Dekorera ditt hem med våra vackra ljuslyktor och andra dekorativa element. Skapa en personlig och välkomnande atmosfär i alla rum.";
    }
    return `Utforska vårt noggrant utvalda sortiment av ${categoryName.toLowerCase()}. Högkvalitativa produkter som gör skillnad i ditt hem.`;
  };

  if (loading) {
    return (
      <SEOPageWrapper>
        <LoadingWrapper>Laddar kategori...</LoadingWrapper>
      </SEOPageWrapper>
    );
  }

  if (error || !category) {
    return (
      <SEOPageWrapper>
        <LoadingWrapper>Kategori kunde inte hittas</LoadingWrapper>
      </SEOPageWrapper>
    );
  }

  const metaTitle = `${category.name} | Premium Kvalitet | Lillaflora`;
  const metaDescription = getCategoryDescription(category.name).substring(0, 160);
  // Use the public products listing URL as canonical, even when this
  // landing page is rendered under a pretty /kategori/{slug} URL.
  const canonicalUrl = `https://lillaflora.nu/produkter?kategori=${categorySlug}`;

  return (
    <SEOPageWrapper>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${category.name}, inredning, hem, svenska produkter, lillaflora`} />

        {/* Open Graph tags */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lillaflora" />

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": category.name,
            "description": getCategoryDescription(category.name),
            "url": canonicalUrl,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": products.length,
              "itemListElement": products.map((product, index) => ({
                "@type": "Product",
                "position": index + 1,
                "name": product.name,
                "description": product.short_description,
                "offers": {
                  "@type": "Offer",
                  "price": product.final_price || product.price,
                  "priceCurrency": "SEK",
                  "availability": (product.inventory_quantity ?? 0) > 0
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock"
                }
              }))
            }
          })}
        </script>
      </Helmet>

      <SEOHeader>
        <div className="container">
          <h1>{getCategoryIcon(category.name)} {category.name}</h1>
          <p className="subtitle">
            {getCategoryDescription(category.name)}
          </p>
        </div>
      </SEOHeader>

      <ContentSection>
        <div className="container">
          <h2>Våra favoriter inom {category.name}</h2>
          <p className="intro-text">
            Handplockade produkter med fokus på kvalitet, design och funktionalitet.
            Alla våra {category.name.toLowerCase()} kommer från pålitliga leverantörer och är testade för att möta våra höga standarder.
          </p>

          <ProductGrid>
            {products.map((product) => (
              <ProductCard key={product.id} onClick={() => handleProductClick(product)}>
                <div className="product-image">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = getCategoryIcon(category.name);
                      }}
                    />
                  ) : (
                    getCategoryIcon(category.name)
                  )}
                </div>
                <div className="product-content">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">
                    {product.short_description || product.description}
                  </p>
                  <div className="product-price">
                    <span className="current-price">
                      {formatPrice(product.final_price || product.price)}
                    </span>
                    {product.has_active_discount && product.price !== product.final_price && (
                      <>
                        <span className="original-price">
                          {formatPrice(product.price)}
                        </span>
                        <span className="discount-badge">REA</span>
                      </>
                    )}
                  </div>
                  <button className="view-product-btn">
                    Visa produkt
                  </button>
                </div>
              </ProductCard>
            ))}
          </ProductGrid>

          {/* SEO copy sections matching plan.md: Presenttips, Skötselråd, Våra favoriter */}
          <section style={{ marginTop: 'var(--spacing-12)' }}>
            <h2>Presenttips inom {category.name}</h2>
            <p className="intro-text" style={{ marginBottom: 'var(--spacing-6)' }}>
              Letar du efter en omtänksam gå bort‑present eller något litet att ta med till värden?
              Välj en av våra favoriter i kategorin {category.name.toLowerCase()} och kombinera gärna med
              produkter från våra populära sektioner för att skapa en färdig present.
            </p>
            <ul style={{ maxWidth: 800, margin: '0 auto var(--spacing-8) auto', lineHeight: 1.7 }}>
              <li>
                Matcha {category.name.toLowerCase()} med{' '}
                <a href="/produkter?kategori=dukning-och-servering">
                  dukning &amp; servering
                </a>{' '}
                för middagar och bjudningar.
              </li>
              <li>
                Kombinera med{' '}
                <a href="/produkter?kategori=doftljus-och-doftpinnar">
                  ljus &amp; doft
                </a>{' '}
                om du vill skapa en helhetskänsla av hemmamys.
              </li>
              <li>
                För extra inspiration, läs våra artiklar i{' '}
                <a href="/floristbloggen">
                  Floristbloggen
                </a>{' '}
                där vi tipsar om presentidéer och dukningsförslag.
              </li>
            </ul>
          </section>

          <section style={{ marginTop: 'var(--spacing-12)' }}>
            <h2>Skötselråd &amp; styling</h2>
            <p className="intro-text" style={{ marginBottom: 'var(--spacing-6)' }}>
              Rätt skötsel gör att dina favoriter håller längre och fortsätter att kännas lika fina som när du
              packade upp dem. Här är några enkla riktlinjer för {category.name.toLowerCase()}.
            </p>
            <ul style={{ maxWidth: 800, margin: '0 auto var(--spacing-8) auto', lineHeight: 1.7 }}>
              <li>
                Läs alltid eventuella instruktioner på produktsidan för specifika material eller ytskikt.
              </li>
              <li>
                Kombinera gärna {category.name.toLowerCase()} med textilier från{' '}
                <a href="/produkter?kategori=textiler">
                  vår kategori med mjuka textilier
                </a>{' '}
                för att skapa balans mellan hårda och mjuka material.
              </li>
              <li>
                I <a href="/floristbloggen">Floristbloggen</a> delar vi stylingtips för olika säsonger – perfekt
                när du vill uppdatera känslan i hemmet utan att byta ut allt.
              </li>
            </ul>
          </section>
        </div>
      </ContentSection>

      <BenefitsSection>
        <div className="container">
          <h2>Varför välja Lillaflora?</h2>
          <BenefitsGrid>
            <BenefitCard>
              <div className="benefit-icon">🚚</div>
              <h3>Snabb leverans</h3>
              <p>Vi skickar dina produkter inom 1-2 arbetsdagar så du snabbt kan njuta av dina nya favoriter.</p>
            </BenefitCard>
            <BenefitCard>
              <div className="benefit-icon">🏆</div>
              <h3>Högsta kvalitet</h3>
              <p>Alla våra produkter är noga utvalda och testade för att garantera långvarig kvalitet och nöjdhet.</p>
            </BenefitCard>
            <BenefitCard>
              <div className="benefit-icon">💝</div>
              <h3>Personlig service</h3>
              <p>Vår kundservice hjälper dig gärna med frågor och ger personliga rekommendationer.</p>
            </BenefitCard>
            <BenefitCard>
              <div className="benefit-icon">🔒</div>
              <h3>Säker betalning</h3>
              <p>Handla tryggt med våra säkra betalningsalternativ och 14 dagars öppet köp.</p>
            </BenefitCard>
          </BenefitsGrid>
        </div>
      </BenefitsSection>

      <CallToAction>
        <div className="container">
          <h2>Upptäck hela sortimentet</h2>
          <p>
            Se alla våra {category.name.toLowerCase()} och hitta dina nya favoriter.
            Vi lägger till nya produkter regelbundet så det finns alltid något nytt att upptäcka.
          </p>
          <button className="cta-button" onClick={handleViewAllProducts}>
            Visa alla {category.name} →
          </button>
        </div>
      </CallToAction>
    </SEOPageWrapper>
  );
};

export default SEOCategoryPage;
